import type { ReactNode, SyntheticEvent, JSX, FormEventHandler } from 'react';
import { useCallback } from 'react';
import type { DefaultValues, FieldValues, SubmitErrorHandler, SubmitHandler, UseFormProps } from 'react-hook-form';
import { useForm, FormProvider } from 'react-hook-form';
import { DevTool } from '@hookform/devtools';
import { config } from '@utils/config';
import { yupResolver } from '@hookform/resolvers/yup';
import type { AnyObjectSchema } from 'yup';

type SubmitDataType = FieldValues;

interface Props<SubmitData extends SubmitDataType> extends UseFormProps<SubmitData> {
  children: ReactNode | ReactNode[];
  defaultValues?: DefaultValues<SubmitData>;
  onSubmit: SubmitHandler<SubmitData>;
  onInvalid?: SubmitErrorHandler<SubmitData>;
  resetOnSubmit?: boolean;
  schema: AnyObjectSchema;
}

export const Form = <FormData extends SubmitDataType>({
  children,
  onSubmit,
  onInvalid,
  resetOnSubmit = false,
  schema,
  reValidateMode = 'onChange',
  ...props
}: Props<FormData>): JSX.Element => {
  const methods = useForm<FormData>({ reValidateMode, resolver: yupResolver(schema), ...props });

  const handleSubmit: FormEventHandler = useCallback(
    (event: SyntheticEvent) => {
      void methods.handleSubmit(
        data => {
          onSubmit(data, event);
          resetOnSubmit && methods.reset();
        },
        errors => onInvalid && onInvalid(errors, event),
      )(event);
    },
    [methods, onInvalid, onSubmit, resetOnSubmit],
  );

  const handleReset = useCallback(() => {
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit} onReset={handleReset}>
        {config.isDev && <DevTool control={methods.control} />}
        {children}
      </form>
    </FormProvider>
  );
};
